import React, { useCallback, useEffect, useState } from 'react';
import client, { endpoints, errText } from '../../api/client';
import { money, toMajorInput } from '../../lib/format';

// THE screen that turns the site from quote-first to booking-first. Setting a
// price makes that service bookable online with a deposit; leaving it blank keeps
// it on the quote form. The banner says so, because otherwise "why can nobody
// book?" looks like a fault rather than an unfilled field.
export default function Services() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // service id whose checklist is open

  const load = useCallback(async () => {
    try {
      const r = await client.get(endpoints.services.all);
      setItems(r.data.items || []);
    } catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = (fn) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  const unpriced = items.filter((s) => !s.quoteOnly && !s.priceFromMinor);

  return (
    <section className="section">
      <div className="section-head"><h1>Services &amp; prices</h1></div>
      {err && <p className="alert alert-err">{err}</p>}

      {unpriced.length > 0 && (
        <div className="card card-warn">
          <h2>{unpriced.length} service{unpriced.length > 1 ? 's have' : ' has'} no price</h2>
          <p>
            Nothing here invented a price for you. An unpriced service cannot take a deposit, so the storefront sends
            those customers to the quote form instead of showing a figure you never agreed to. Type a price below and
            instant booking switches on for that service immediately.
          </p>
        </div>
      )}

      <table className="table">
        <thead>
          <tr><th>Service</th><th>Category</th><th>For</th><th>Price from</th><th>Unit</th><th>Deposit %</th><th>Mode</th><th>Checklist</th><th>Live</th></tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s._id}>
              <td><strong>{s.name}</strong><div className="muted small">{s.key}</div></td>
              <td>{s.category.replace(/_/g, ' ')}</td>
              <td>{s.channel}</td>
              <td>
                <form className="inline-form" onSubmit={(e) => {
                  e.preventDefault();
                  const v = e.target.elements.price.value;
                  act(() => client.put(endpoints.services.update(s._id), { priceFrom: v === '' ? null : v }))();
                }}>
                  <input name="price" type="number" min="0" step="1" defaultValue={toMajorInput(s.priceFromMinor)} placeholder="unset" />
                  <button className="link-btn" disabled={busy}>save</button>
                </form>
                <div className="muted small">{s.priceFromMinor ? money(s.priceFromMinor) : 'price on assessment'}</div>
              </td>
              <td>
                <select value={s.priceUnit} disabled={busy}
                        onChange={(e) => act(() => client.put(endpoints.services.update(s._id), { priceUnit: e.target.value }))()}>
                  {['visit', 'room', 'sqm', 'hour'].map((u) => <option key={u} value={u}>per {u}</option>)}
                </select>
              </td>
              <td>
                <form className="inline-form" onSubmit={(e) => {
                  e.preventDefault();
                  const v = e.target.elements.pct.value;
                  act(() => client.put(endpoints.services.update(s._id), { depositPct: v === '' ? null : v }))();
                }}>
                  <input name="pct" type="number" min="0" max="100" defaultValue={s.depositPct ?? ''} placeholder="default" />
                  <button className="link-btn" disabled={busy}>save</button>
                </form>
              </td>
              <td>
                <label className="check">
                  <input type="checkbox" checked={!!s.quoteOnly} disabled={busy}
                         onChange={() => act(() => client.put(endpoints.services.update(s._id), { quoteOnly: !s.quoteOnly }))()} />
                  quote only
                </label>
              </td>
              <td>
                <button className="link-btn" onClick={() => setEditing(editing === s._id ? null : s._id)}>
                  {(s.checklist || []).length} item{(s.checklist || []).length === 1 ? '' : 's'}
                </button>
              </td>
              <td>
                <label className="check">
                  <input type="checkbox" checked={!!s.active} disabled={busy}
                         onChange={() => act(() => client.put(endpoints.services.update(s._id), { active: !s.active }))()} />
                  active
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (() => {
        const s = items.find((x) => x._id === editing);
        if (!s) return null;
        return (
          <div className="card">
            <h2>Quality checklist — {s.name}</h2>
            <p className="muted">
              This is what your team signs off before leaving, and a job cannot be marked complete while a required item
              is unticked. It is copied onto each booking when it is made, so editing here never rewrites a past job&apos;s
              record.
            </p>
            <ChecklistEditor service={s} onSave={(checklist) => act(() => client.put(endpoints.services.checklist(s._id), { checklist }))()} busy={busy} />
          </div>
        );
      })()}
    </section>
  );
}

function ChecklistEditor({ service, onSave, busy }) {
  const [rows, setRows] = useState(() => (service.checklist || []).map((c) => ({ label: c.label, required: c.required !== false })));
  return (
    <>
      <ul className="checklist">
        {rows.map((r, i) => (
          <li key={i}>
            <input value={r.label} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
            <label className="check">
              <input type="checkbox" checked={r.required} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))} />
              required
            </label>
            <button className="link-btn" onClick={() => setRows(rows.filter((_, j) => j !== i))}>remove</button>
          </li>
        ))}
        {rows.length === 0 && <li className="muted">No items yet.</li>}
      </ul>
      <div className="btn-row">
        <button className="btn-ghost" onClick={() => setRows([...rows, { label: '', required: true }])}>Add item</button>
        <button className="btn-primary" disabled={busy} onClick={() => onSave(rows.filter((r) => r.label.trim()))}>Save checklist</button>
      </div>
    </>
  );
}
